import React, { FC } from 'react'
import classNames from 'classnames'
import { ExternalLinkIcon } from '@/atoms/Icons/ExternalLinkIcon'
import { Image } from '@/atoms/Image'
import { slugs } from '@/constants/slugs'
import { ExperimentVariation, GBFadeInExperiment } from '@/experimentation'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { HomesteadSeriesPrizeItemsTabContent } from '@/views/PreSales/PreSalesGiveawayView/components/Tabs/HomesteadSeriesPrizeItemsTabContent'
import { TkokPrizeItemsTabContent } from '@/views/PreSales/PreSalesGiveawayView/components/Tabs/TkokPrizeItemsTabContent'
import {
  getDavidIntroText,
  getDavidOutroText,
  getDavidPrizeText,
  getIwasIntroText,
  getIwasPrizeText,
  getIwasSecondPrizeText,
  getSoloMioIntroText,
  getSoloMioPrizeText,
} from '@/views/PreSales/PreSalesGiveawayView/config/giveaway-prizes/projects'
import { useGiveawayContext } from '@/views/PreSales/PreSalesGiveawayView/context/GiveawayContext'
import { GiveawayPrizeItem } from '@/views/PreSales/PreSalesGiveawayView/hooks/useGiveawayConfigData'
import { calculatePrizeValue } from '@/views/PreSales/PreSalesGiveawayView/utils'
import {
  GEyebrowSM,
  GHeadingSM,
  GParagraphLG,
  GParagraphMD,
  GParagraphSM,
  GResponsiveText,
  GTitleLG,
  GTitleMobile,
  GTitleXL,
  GTitleXXL,
} from '../UI/Typography'

interface Props {
  prizes?: {
    grandPrizes?: GiveawayPrizeItem[]
    firstPlacePrizes?: GiveawayPrizeItem[]
    secondPlacePrizes?: GiveawayPrizeItem[]
    prizes?: GiveawayPrizeItem[]
    numberOfWinners?: {
      firstPlace?: string
      secondPlace?: string
    }
    grandPrizeText?: string
    firstPlaceText?: string
    secondPlaceText?: string
  } | null
}

const renderPrizeSection = (prizes: GiveawayPrizeItem[]) => {
  return prizes.map((prizeItem, index) => (
    <PrizeItem
      key={index}
      image={prizeItem.image}
      alt={prizeItem.alt}
      title={prizeItem.title}
      value={prizeItem.value}
      url={prizeItem.url}
      sponsorName={prizeItem.sponsorName}
      representativePrizeShown={prizeItem.representativePrizeShown}
    />
  ))
}

const kokPrizeTitleFilterList = new Set(['London and Holy Land Trip', '$25,000 in BitCoin'])

export const PrizeItemsTab: FC<Props> = ({ prizes }) => {
  const { t } = useTranslate('tickets')
  const { theatricalSlug, experiments, isActive } = useGiveawayContext()
  const isFreddieExperiment = experiments?.['freddie-wong-giveaway-cutout']

  const grandPrizeValue = React.useMemo(() => calculatePrizeValue(prizes?.grandPrizes, true), [prizes?.grandPrizes])

  const firstPlacePrizeValue = React.useMemo(
    () => calculatePrizeValue(prizes?.firstPlacePrizes),
    [prizes?.firstPlacePrizes],
  )

  const secondPlacePrizeValue = React.useMemo(
    () => calculatePrizeValue(prizes?.secondPlacePrizes),
    [prizes?.secondPlacePrizes],
  )

  if (theatricalSlug === slugs.homesteadSeries) {
    return <HomesteadSeriesPrizeItemsTabContent />
  }

  if (theatricalSlug === slugs.kingOfKings && prizes?.prizes) {
    const tkokPrizes = prizes.prizes.filter((prize) => !kokPrizeTitleFilterList.has(prize.title))
    return <TkokPrizeItemsTabContent prizes={tkokPrizes} />
  }

  if (theatricalSlug === slugs.soloMio && prizes?.grandPrizes && prizes.grandPrizes.length > 0) {
    return (
      <div className="mx-4 mb-20 mt-10 flex flex-col gap-14 md:ml-11 md:mr-8 md:mt-20">
        <div>
          <GResponsiveText
            color="giveaway-text-primary-dark"
            className="mb-3"
            MobileTextComponent={GTitleMobile}
            DesktopTextComponent={GHeadingSM}
          >
            {t('soloMioLoversGetaway', 'Solo Mio Lovers Getaway')}
          </GResponsiveText>
          {getSoloMioIntroText(t)}
        </div>
        {getSoloMioPrizeText(t)}
        <div className="grid gap-14 lg:grid-cols-2">{renderPrizeSection(prizes.grandPrizes)}</div>
      </div>
    )
  }
  if (theatricalSlug === slugs.david && prizes?.grandPrizes && prizes.grandPrizes.length > 0) {
    return (
      <div className="px-11 pt-20 md:pr-8">
        <GResponsiveText
          color="giveaway-text-primary-dark"
          className="mb-3"
          MobileTextComponent={GTitleLG}
          DesktopTextComponent={GHeadingSM}
        >
          {t('DavidGrandPrize', 'David Christmas Tour Giveaway')}
        </GResponsiveText>
        {getDavidIntroText(t)}

        {getDavidPrizeText(t)}
        {getDavidOutroText(t)}
        <Translate i18nKey={'EstimatedValueDavidV2'} t={t}>
          <GResponsiveText
            color="giveaway-text-subtle-light"
            className={'py-8 !font-bold'}
            MobileTextComponent={GParagraphMD}
            DesktopTextComponent={GParagraphLG}
          >
            Estimated value: $60,000
          </GResponsiveText>
        </Translate>
        <div className="mb-10 grid gap-14 lg:grid-cols-2">{renderPrizeSection(prizes.grandPrizes)}</div>
      </div>
    )
  }
  if (theatricalSlug === slugs.iWasAStranger && prizes?.grandPrizes && prizes.grandPrizes.length > 0) {
    return (
      <div className="px-11 pt-20 md:pr-8">
        <GResponsiveText
          color="giveaway-text-primary-dark"
          className="mb-3"
          MobileTextComponent={GTitleLG}
          DesktopTextComponent={GHeadingSM}
        >
          {t('IwasGrandPrize2', 'Paths of Hope Trip')}
        </GResponsiveText>
        {getIwasIntroText(t)}

        {getIwasPrizeText(t)}
        <Translate i18nKey={'EstimatedValueIwasV2'} t={t}>
          <GResponsiveText
            color="giveaway-text-subtle-light"
            className={'py-8 !font-bold'}
            MobileTextComponent={GParagraphMD}
            DesktopTextComponent={GParagraphLG}
          >
            Estimated value: $20,000
          </GResponsiveText>
        </Translate>
        {getIwasSecondPrizeText(t)}
        <div className="my-10 grid gap-14 lg:grid-cols-2">{renderPrizeSection(prizes.grandPrizes)}</div>
      </div>
    )
  }
  if (theatricalSlug === slugs.truthAndTreason && prizes?.grandPrizes && prizes.grandPrizes.length > 0) {
    return (
      <div className="w-full px-4">
        <div className="my-10 md:mt-16">
          <GResponsiveText
            color="giveaway-text-primary-dark"
            MobileTextComponent={GTitleLG}
            DesktopTextComponent={GHeadingSM}
          >
            {t('TnTUnforgettablePrize', 'One Unforgettable Prize: The Truth & Treason VIP Trip Giveaway')}
          </GResponsiveText>
          <Translate i18nKey={'truthAndTreasonPrizeTabExplainer'} t={t}>
            <GResponsiveText
              color="giveaway-text-subtle-light"
              className={'mb-6 pt-3'}
              MobileTextComponent={GParagraphMD}
              DesktopTextComponent={GParagraphLG}
            >
              To celebrate the release of Truth & Treason, Angel is giving away an unforgettable historical journey.
              Pre-order your tickets today and you could win a 10-day luxury tour for two across Germany—from Hamburg to
              Berlin to Munich — tracing the real-life story of Helmuth Hübener and exploring the broader fight against
              Nazi oppression.
            </GResponsiveText>
          </Translate>
        </div>
        <div className="my-10 md:mt-16">
          <GResponsiveText
            color="giveaway-text-primary-dark"
            MobileTextComponent={GTitleLG}
            DesktopTextComponent={GHeadingSM}
          >
            {t('TnTGrandPrize', "Grand Prize – Helmuth Hübener's Legacy Tour (for two)")}
          </GResponsiveText>
          <ul className={'list-disc pl-4'}>
            {prizes.grandPrizes.map((prizeItem, index) => (
              <li key={index}>
                <GResponsiveText
                  color="giveaway-text-subtle-light"
                  className={'mb-6 pt-3'}
                  MobileTextComponent={GParagraphMD}
                  DesktopTextComponent={GParagraphLG}
                >
                  {prizeItem.title}
                </GResponsiveText>
              </li>
            ))}
          </ul>
          <Translate i18nKey={'EstimatedValueTnT'} t={t}>
            <GResponsiveText
              color="giveaway-text-subtle-light"
              className={'pb-10 pt-3 !font-bold'}
              MobileTextComponent={GParagraphMD}
              DesktopTextComponent={GParagraphLG}
            >
              Total value: $15,000–$20,000.
            </GResponsiveText>
          </Translate>
          <div className="mb-10 grid gap-14 lg:grid-cols-2">{renderPrizeSection(prizes.grandPrizes)}</div>
        </div>
      </div>
    )
  }
  if (theatricalSlug === slugs.senior)
    return (
      <div className="w-full px-4">
        <div className="my-10 md:mt-16">
          <GResponsiveText
            color="giveaway-text-primary-dark"
            MobileTextComponent={GTitleLG}
            DesktopTextComponent={GTitleXL}
          >
            {t('seniorDontmiss', "Don't Miss the Kickoff! Pre-Order Your Tickets to The Senior Now!")}
          </GResponsiveText>
          <Translate i18nKey={'seniorPrizeTabExplainerV3'} t={t}>
            <GResponsiveText
              color="giveaway-text-subtle-light"
              className={'mb-6 pt-3'}
              MobileTextComponent={GParagraphMD}
              DesktopTextComponent={GParagraphLG}
            >
              This fall, Angel is celebrating The Senior — the incredible true story of Mike Flynt, the oldest
              linebacker in college football history — with the ultimate football fan giveaway. By pre-ordering your
              tickets, you’ll not only be first in line to see a story about second chances and unfinished business, but
              you’ll also earn a shot at prizes big enough to make your season unforgettable!
            </GResponsiveText>
            <GResponsiveText
              color="giveaway-text-subtle-light"
              className={'mb-6 pt-3'}
              MobileTextComponent={GParagraphMD}
              DesktopTextComponent={GParagraphLG}
            >
              From weekly cash wins to full-ride scholarships and the ultimate $50,000 Big Game prize, every ticket
              brings you closer to a life-changing score.
            </GResponsiveText>
          </Translate>
        </div>
        <div className="my-10 md:mt-16">
          <GResponsiveText
            color="giveaway-text-primary-dark"
            MobileTextComponent={GTitleLG}
            DesktopTextComponent={GTitleXL}
          >
            {t('seniorGrandPrize', 'Grand Prize – VIP Big Game Experience')}
          </GResponsiveText>
          <Translate i18nKey={'seniorGrandPrizedesc2'} t={t}>
            <GResponsiveText
              color="giveaway-text-subtle-light"
              className={'mb-6 pt-3'}
              MobileTextComponent={GParagraphMD}
              DesktopTextComponent={GParagraphLG}
            >
              Win $50,000 cash to purchase a trip for two to the biggest game of the year! That’s enough to buy
              yourself: First-class flights, luxury limo service, premium lower-bowl seats, 5-star hotel for 3 nights,
              fine dining, merch, and more!
            </GResponsiveText>
          </Translate>
          {prizes?.grandPrizes && renderPrizeSection(prizes.grandPrizes)}
        </div>
        <div className="my-10 md:mt-16">
          <GResponsiveText
            color="giveaway-text-primary-dark"
            MobileTextComponent={GTitleLG}
            DesktopTextComponent={GTitleXL}
          >
            {t('seniorWeeklyPrizeTitle', 'Weekly Giveaway - $10,000 Cash for College Football Season Tickets')}
          </GResponsiveText>
          <Translate i18nKey={'seniorWeeklyPrizedesc2'} t={t}>
            <GResponsiveText
              color="giveaway-text-subtle-light"
              className={'mb-6 pt-3'}
              MobileTextComponent={GParagraphMD}
              DesktopTextComponent={GParagraphLG}
            >
              Every week, one lucky fan wins $10,000 in cash—perfect for season tickets to their favorite college
              football team. Six winners. Six chances to score big.
            </GResponsiveText>
          </Translate>
          {prizes?.firstPlacePrizes && renderPrizeSection(prizes.firstPlacePrizes)}
        </div>
        <div className="my-10 md:mt-16">
          <GResponsiveText
            color="giveaway-text-primary-dark"
            MobileTextComponent={GTitleLG}
            DesktopTextComponent={GTitleXL}
          >
            {t('seniorScholarshipPrize', 'Full-Ride Scholarships to Sul Ross State University– Five Winners')}
          </GResponsiveText>

          <GResponsiveText
            color="giveaway-text-subtle-light"
            className={'mb-6 pt-3'}
            MobileTextComponent={GParagraphMD}
            DesktopTextComponent={GParagraphLG}
          >
            {t(
              'seniorScholarshipPrizeDesc2',
              'Win a full-ride scholarship (tuition, room, board) to Sul Ross State University. Five winners. Five dreams funded.',
            )}
          </GResponsiveText>
          {prizes?.secondPlacePrizes && renderPrizeSection(prizes.secondPlacePrizes)}
        </div>
      </div>
    )
  return (
    <div className="w-full px-4">
      {theatricalSlug === slugs.youngWashington && isActive && (
        <div className="my-10 md:mt-16">
          <GResponsiveText
            color="giveaway-text-primary-dark"
            MobileTextComponent={GTitleLG}
            DesktopTextComponent={GTitleXL}
          >
            {t('youngWashingtonComestoTheaters', 'YOUNG WASHINGTON comes to theaters July 4th, 2026')}
          </GResponsiveText>
          <Translate i18nKey={'youngWashingtonPrizeTabExplainerV2'} t={t}>
            <GResponsiveText
              color="giveaway-text-subtle-light"
              className={'mb-6 pt-3'}
              MobileTextComponent={GParagraphSM}
              DesktopTextComponent={GParagraphMD}
            >
              One of the most important stories in our nation&apos;s history is coming to the silver screen next 4th of
              July. By getting your tickets early, you ensure this epic tale is seen from sea to shining sea in as many
              theaters as possible. Make your plans for next year NOW & be entered to win amazing prizes. Over 1,000
              winners will be selected—the earlier you buy, the more entries you get.
            </GResponsiveText>
            <GResponsiveText
              color="giveaway-text-subtle-light"
              className={'mb-6 pt-3'}
              MobileTextComponent={GParagraphSM}
              DesktopTextComponent={GParagraphMD}
            >
              You can help spread this important message of patriotism, faith, and freedom by purchasing your tickets a
              full year in advance. Join us in showing theaters, and the world, that the story of Young Washington is
              the movie America needs for its 250th birthday.
            </GResponsiveText>
          </Translate>
        </div>
      )}
      {prizes?.prizes && (
        <>
          <div className="my-10 md:mt-16">
            <GResponsiveText
              color="giveaway-text-primary-dark"
              MobileTextComponent={GTitleLG}
              DesktopTextComponent={GTitleXL}
            >
              {t('prizeItems', 'Prize Items')}
            </GResponsiveText>
            <GResponsiveText
              color="giveaway-text-subtle-light"
              className="mt-3"
              MobileTextComponent={GParagraphSM}
              DesktopTextComponent={GParagraphMD}
            >
              {t('prizeItemsDescriptionLabel', 'Below is the collection of the items included in this giveaway.')}
            </GResponsiveText>
          </div>
          <div className="mb-10 grid gap-6 lg:grid-cols-2">{renderPrizeSection(prizes.prizes)}</div>
        </>
      )}
      {prizes?.grandPrizes && (
        <>
          <div className="my-10 md:mt-16">
            {isFreddieExperiment ? (
              <Translate t={t} i18nKey={'FreddieGrandPrizeItems'}>
                <GResponsiveText
                  color="giveaway-text-primary-dark"
                  MobileTextComponent={GTitleXL}
                  DesktopTextComponent={GTitleXXL}
                >
                  <span className="text-giveaway-secondary-accent">Grand Prize</span> winner gets:
                </GResponsiveText>
              </Translate>
            ) : (
              <GResponsiveText
                color="giveaway-text-primary-dark"
                MobileTextComponent={GTitleLG}
                DesktopTextComponent={GTitleXL}
              >
                {t('grandPrizeItems', 'Grand Prize Items')}
              </GResponsiveText>
            )}
            <GResponsiveText
              color="giveaway-text-subtle-light"
              className="mt-3"
              MobileTextComponent={GParagraphSM}
              DesktopTextComponent={GParagraphMD}
            >
              {prizes.grandPrizeText
                ? prizes.grandPrizeText
                : t(
                    'grandPrizeItemsDescriptionLabelv2',
                    'Below is the collection of the items included in the Grand Prize giveaway, over {{grandPrizeValue}} in prizes.',
                    { grandPrizeValue },
                  )}
            </GResponsiveText>
          </div>
          <div className="mb-10 grid gap-6 lg:grid-cols-2">{renderPrizeSection(prizes.grandPrizes)}</div>
        </>
      )}
      {prizes?.firstPlacePrizes && (
        <>
          <div className="my-10 md:mt-16">
            {isFreddieExperiment ? (
              <Translate t={t} i18nKey={'FreddieFirstPlacePrizeItems'}>
                <GResponsiveText
                  color="giveaway-text-primary-dark"
                  MobileTextComponent={GTitleXL}
                  DesktopTextComponent={GTitleXXL}
                >
                  <span className="text-giveaway-secondary-accent">First Place</span> winner gets:
                </GResponsiveText>
              </Translate>
            ) : (
              <GResponsiveText
                color="giveaway-text-primary-dark"
                MobileTextComponent={GTitleLG}
                DesktopTextComponent={GTitleXL}
              >
                {t('firstPlacePrizeItemsGiveaway2', '1ST Place Prizes')}
              </GResponsiveText>
            )}
            <GResponsiveText
              color="giveaway-text-subtle-light"
              className="mt-3"
              MobileTextComponent={GParagraphSM}
              DesktopTextComponent={GParagraphMD}
            >
              {prizes.firstPlaceText
                ? prizes.firstPlaceText
                : t(
                    'firstPlacePrizeItemsDescriptionLabelv2',
                    'Below is the collection of the items included in the {{firstPlaceWinners}} First Place Prizes, valued at {{firstPlacePrizeValue}} each.',
                    {
                      firstPlacePrizeValue,
                      firstPlaceWinners: prizes?.numberOfWinners?.firstPlace || '',
                    },
                  )}
            </GResponsiveText>
          </div>
          <div className="mb-10 grid gap-6 lg:grid-cols-2">{renderPrizeSection(prizes.firstPlacePrizes)}</div>
        </>
      )}
      {prizes?.secondPlacePrizes && (
        <>
          <div className="my-10 md:mt-16">
            <GResponsiveText
              color="giveaway-text-primary-dark"
              MobileTextComponent={GTitleLG}
              DesktopTextComponent={GTitleXL}
            >
              {t('secondPlacePrizeItemsGiveaway2', '2ND Place Prizes')}
            </GResponsiveText>
            <GResponsiveText
              color="giveaway-text-subtle-light"
              className="mt-3"
              MobileTextComponent={GParagraphSM}
              DesktopTextComponent={GParagraphMD}
            >
              {prizes.secondPlaceText
                ? prizes.secondPlaceText
                : t(
                    'secondPlacePrizeItemsDescriptionLabelv2',
                    '{{secondPlaceWinners}} Second Place Prize winners, valued at {{secondPlacePrizeValue}} each.',
                    {
                      secondPlacePrizeValue,
                      secondPlaceWinners: prizes?.numberOfWinners?.secondPlace || '',
                    },
                  )}
            </GResponsiveText>
          </div>
          <div className="mb-10 grid gap-6 lg:grid-cols-2">{renderPrizeSection(prizes.secondPlacePrizes)}</div>
        </>
      )}
    </div>
  )
}

interface PrizeItemProps {
  image: string
  alt: string
  title: string
  value?: string
  url?: string
  sponsorName?: string
  representativePrizeShown?: boolean
}

const PrizeItem = ({ image, alt, title, value, url, sponsorName, representativePrizeShown }: PrizeItemProps) => {
  const { t } = useTranslate('tickets')
  const { theatricalSlug } = useGiveawayContext()

  const imageContent = (
    <div
      className={classNames(
        ' relative overflow-hidden',
        theatricalSlug === slugs.sketch ? 'rounded-none' : 'rounded-lg',
        theatricalSlug === slugs.senior && 'md:w-[440px] md:h-[230px] w-[360px] h-[188px]',
        theatricalSlug !== slugs.senior && 'aspect-h-9 aspect-w-16',
      )}
    >
      {theatricalSlug !== slugs.senior && (
        <Image
          src={image}
          alt={alt}
          fill
          className="object-cover transition-transform duration-300 "
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
        />
      )}
      {theatricalSlug === slugs.senior && (
        <Image
          src={image}
          alt={alt}
          fill
          className="!h-[188px] !w-[360px] object-cover transition-transform duration-300 group-hover:scale-105 md:!h-[230px] md:!w-[440px]"
        />
      )}
      {representativePrizeShown && (
        <div className="absolute inset-0 ">
          <div className="relative h-full w-full">
            <Image
              src={'/v1747076634/repprizebig_ty4oa1.png'}
              alt={'Representitive Prize Shown'}
              fill
              className="object-cover transition-transform duration-300 group-hover:scale-105"
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            />
          </div>
        </div>
      )}
      {url && sponsorName && (
        <GBFadeInExperiment experimentName="prize-sponsor-links" defaultValue={false}>
          <ExperimentVariation variation={true}>
            <div className="absolute inset-0 flex items-end justify-end p-[10px]">
              <div className="inline-flex items-center gap-1 rounded-none bg-white/90 px-2 py-1 text-xs font-medium text-giveaway-primary shadow-sm backdrop-blur-sm transition-all duration-200 group-hover:bg-white group-hover:shadow-md">
                <span>{t('visitSponsorName', 'Visit {{sponsorName}}', { sponsorName })}</span>
                <ExternalLinkIcon size={12} className="relative top-px" />
              </div>
            </div>
          </ExperimentVariation>
        </GBFadeInExperiment>
      )}
    </div>
  )

  return (
    <div className="group flex flex-col pb-4">
      <GBFadeInExperiment experimentName="prize-sponsor-links" defaultValue={false}>
        <ExperimentVariation variation={true}>
          {url ? (
            <a href={url} target="_blank" rel="noopener noreferrer" className="relative overflow-hidden rounded-lg">
              {imageContent}
            </a>
          ) : (
            imageContent
          )}
        </ExperimentVariation>
        <ExperimentVariation variation={false}>{imageContent}</ExperimentVariation>
      </GBFadeInExperiment>
      <div
        className={classNames(
          'flex justify-between gap-4 pb-1 pt-2',
          theatricalSlug === slugs.senior && 'max-w-lg !justify-start',
        )}
      >
        {theatricalSlug === slugs.truthAndTreason && (
          <div className="w-full">
            <GEyebrowSM color="giveaway-text-subtle-light">{t('item', 'item')}</GEyebrowSM>
            <GParagraphMD color={theatricalSlug === slugs.sketch ? 'giveaway-primary' : 'giveaway-text-primary-dark'}>
              {title}
            </GParagraphMD>
          </div>
        )}
        {theatricalSlug != slugs.truthAndTreason && (
          <div className="w-2/3">
            <GEyebrowSM color="giveaway-text-subtle-light">{t('item', 'item')}</GEyebrowSM>
            <GParagraphMD color={theatricalSlug === slugs.sketch ? 'giveaway-primary' : 'giveaway-text-primary-dark'}>
              {title}
            </GParagraphMD>
          </div>
        )}
        {value && (
          <div>
            <GEyebrowSM color="giveaway-subtle">{t('value', 'value')}</GEyebrowSM>
            <GParagraphMD color={theatricalSlug === slugs.sketch ? 'giveaway-primary' : 'giveaway-text-primary-dark'}>
              {value}
            </GParagraphMD>
          </div>
        )}
      </div>
    </div>
  )
}
